// Basic
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
// Store & actions
import useRemoteCollectionStore from '@/app/store/modules/remoteCollection';
import useRootOrganizationStore from '@/app/store/rootOrganization';
import actionsRemoteCollections from '../../api/actionsRemoteCollections';
// Components
import DataTable from '../../../../../common-utils/components/renders/dataTable/DataTable';
// Common
import remoteCollectionFields from '../../fields/remoteCollectionFields';
import generateNestedFieldAccessor from '@/common-utils/functions/generateNestedFieldAccessor';
import getInitialQuery from '@/common-utils/components/renders/dataTable/utils/getInitialQuery';
import mergeQuery from '@/common-utils/components/renders/dataTable/utils/mergeQuery';
import Permissions from '@/app/permissions/Permissions';
import REMOTE_COLLECTION_RECONCILIATION_CODE from '../../enums/RemoteCollectionReconciliationCode';
import { columnsRemoteCollections, columnsRemoteCollectionsId } from './columnDef/columnsRemoteCollections';
import { REMOTE_COLLECTION } from '../../api/apiUrls';

/**
 * @param root0
 * @param root0.organizationId
 * @param root0.pageActions
 * @param root0.filtersEventName
 * @returns {JSX} RemoteCollectionsTable
 */
function RemoteCollectionsTable({
    organizationId = undefined,
    pageActions = undefined,
    filtersEventName
}) {
    // Store
    const rootOrganization = useRootOrganizationStore(s => s.rootOrganization);
    const { refetchRemoteCollections: refetchFromStore,
        storeRefetchRemoteCollections, query: queryStored, storeQuery } = useRemoteCollectionStore();

    // Constants
    const QUERY_PRESETS = {
        filters: [
            organizationId ? {
                key: 'provider',
                value: organizationId,
                keepOnReset: true,
                hidden: true
            } : null,
            {
                key: generateNestedFieldAccessor(remoteCollectionFields.RECONCILIATION_CODE),
                value: REMOTE_COLLECTION_RECONCILIATION_CODE.NOT_EMPTY.name,
                keepOnReset: true
            }
        ].filter(Boolean)
    };
    const initialQuery = getInitialQuery(QUERY_PRESETS);

    // State
    const [query, setQuery] = useState(mergeQuery(initialQuery, queryStored));
    const [launchRefetch, setLaunchRefetch] = useState(true);

    // Api Calls
    const { data, isFetching, refetch } = actionsRemoteCollections.getRemoteCollections(query, false);

    // Hooks
    useEffect(() => {
        if (rootOrganization && (launchRefetch || refetchFromStore)) {
            refetch();
            setLaunchRefetch(false);
            storeRefetchRemoteCollections(false);
        }
    }, [query, launchRefetch, refetchFromStore]);

    const allColumns = columnsRemoteCollections({ query, setQuery, queryStored, storeQuery, setLaunchRefetch, filtersEventName });

    // Render
    return (
        <DataTable
            filtersEventName={filtersEventName}
            columns={allColumns}
            data={data}
            dataFetchingIsLoading={isFetching}
            columnsId={columnsRemoteCollectionsId}
            initialQuery={initialQuery}
            query={query}
            setQuery={setQuery}
            queryStored={queryStored}
            storeQuery={storeQuery}
            setLaunchRefetch={setLaunchRefetch}
            hasPartialResults={true}

            pageActions={pageActions}
            exportProps={!organizationId ?
                {
                    permissions: [Permissions.EXPORT_REMOTE_COLLECTIONS.name],
                    api: REMOTE_COLLECTION.EXPORT
                } : null
            }
        />
    );
}

RemoteCollectionsTable.propTypes = {
    organizationId: PropTypes.string,
    pageActions: PropTypes.node,
    filtersEventName: PropTypes.string
};

export default RemoteCollectionsTable;